import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useSpeechRecognition } from '../components/AudioRecorder/useSpeechRecognition';
import { useAudioProcessor } from '../components/AudioRecorder/useAudioProcessor';
import { compareText } from '../components/AudioRecorder/compareText';
import { handleError } from './errors/errorUtils';

const AudioRecorderConfigContext = createContext();
const AudioRecorderStateContext = createContext();

const isMediaRecorderSupported = () => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm');

// Определяем iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Проверка HTTPS
const isSecureContext = () => {
  return window.isSecureContext || window.location.protocol === 'https:' || 
         window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

export const AudioRecorderProvider = ({ children, userId, language = 'en', source }) => {
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [permissionState, setPermissionState] = useState('prompt'); // 'prompt', 'granted', 'denied'
  const streamRef = useRef(null);
  const isRequestingPermission = useRef(false);

  useEffect(() => {
    const supported = isMediaRecorderSupported();
    const secure = isSecureContext();
    
    if (!secure) {
      handleError({
        code: 'ERROR_INSECURE_CONTEXT',
        error: new Error('Microphone requires HTTPS'),
        context: {
          module: 'audio_recorder',
          message: 'Микрофон доступен только через HTTPS',
          userId,
          action: 'check_security',
        },
        showToUser: true,
      });
    }
    
    setIsSupported(supported && secure);
  }, [userId]);

  // Проверяем состояние разрешения через Permissions API (если доступно)
  useEffect(() => {
    const checkPermissionState = async () => {
      if (!navigator.permissions) return;
      
      try {
        const result = await navigator.permissions.query({ name: 'microphone' });
        setPermissionState(result.state);
        
        result.addEventListener('change', () => {
          setPermissionState(result.state);
          if (result.state === 'denied') {
            setPermissionGranted(false);
            // Останавливаем поток если разрешение отозвано
            if (streamRef.current) {
              streamRef.current.getTracks().forEach(track => track.stop());
              streamRef.current = null;
            }
          }
        });
      } catch (error) {
        // Permissions API может быть недоступен на некоторых браузерах
        console.log('Permissions API not available:', error);
      }
    };

    if (isSupported) {
      checkPermissionState();
    }
  }, [isSupported]);

  useEffect(() => {
    async function requestMicrophonePermission() {
      // Избегаем множественных одновременных запросов
      if (isRequestingPermission.current) return;
      isRequestingPermission.current = true;

      try {
        const constraints = {
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            // Для iOS важно не запрашивать специфичные параметры
            ...(isIOS() ? {} : {
              sampleRate: 48000,
              channelCount: 1
            })
          }
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        
        // НЕ отключаем треки сразу - это может вызвать проблемы на iOS
        // Вместо этого оставляем их активными, но отключим при старте записи
        
        setPermissionGranted(true);
        setPermissionState('granted');
      } catch (error) {
        setPermissionGranted(false);
        
        // Детальная обработка ошибок
        let errorMessage = 'Ошибка доступа к микрофону';
        let errorCode = 'ERROR_MIC_PERMISSION';
        
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          errorMessage = 'Доступ к микрофону запрещен. Проверьте настройки браузера.';
          errorCode = 'ERROR_MIC_PERMISSION_DENIED';
          setPermissionState('denied');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          errorMessage = 'Микрофон не найден';
          errorCode = 'ERROR_MIC_NOT_FOUND';
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          errorMessage = 'Микрофон уже используется другим приложением';
          errorCode = 'ERROR_MIC_IN_USE';
        } else if (error.name === 'OverconstrainedError') {
          errorMessage = 'Не удалось применить настройки микрофона';
          errorCode = 'ERROR_MIC_CONSTRAINTS';
        } else if (error.name === 'SecurityError') {
          errorMessage = 'Доступ запрещен по соображениям безопасности (требуется HTTPS)';
          errorCode = 'ERROR_MIC_SECURITY';
        }

        handleError({
          code: errorCode,
          error,
          context: {
            module: 'audio_recorder',
            message: errorMessage,
            userId,
            action: 'permission_error',
            errorName: error.name,
            isIOS: isIOS(),
            isSecure: isSecureContext(),
            userAgent: navigator.userAgent,
          },
          showToUser: true,
        });
      } finally {
        isRequestingPermission.current = false;
      }
    }

    if (isSupported && permissionState !== 'denied') {
      requestMicrophonePermission();
    }

    // Обработчик видимости - убираем индикатор микрофона когда вкладка неактивна
    const handleVisibilityChange = () => {
      if (document.hidden && streamRef.current) {
        // На iOS оставляем поток активным, чтобы не потерять разрешение
        // На Android и десктопах - полностью останавливаем для скрытия индикатора
        if (!isIOS()) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      } else if (!document.hidden && !streamRef.current && permissionGranted) {
        // При возврате на вкладку переинициализируем поток (только для не-iOS)
        if (!isIOS()) {
          const reinitializeStream = async () => {
            try {
              const constraints = {
                audio: {
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true,
                  sampleRate: 48000,
                  channelCount: 1
                }
              };
              const stream = await navigator.mediaDevices.getUserMedia(constraints);
              streamRef.current = stream;
            } catch (error) {
              console.error('Failed to reinitialize stream:', error);
              // Не логируем как критичную ошибку, т.к. это фоновая реинициализация
            }
          };
          reinitializeStream();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Останавливаем поток только при размонтировании компонента
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [isSupported, userId, permissionState]);

  const configValue = { 
    permissionGranted, 
    isSupported, 
    streamRef,
    permissionState,
    isIOS: isIOS()
  };

  return (
    <AudioRecorderConfigContext.Provider value={configValue}>
      <AudioRecorderStateProvider userId={userId} language={language} source={source}>
        {children}
      </AudioRecorderStateProvider>
    </AudioRecorderConfigContext.Provider>
  );
};

const AudioRecorderStateProvider = ({ children, userId, language, source }) => {
  const { permissionGranted, isSupported, streamRef, isIOS } = useAudioRecorderConfig();
  const [isRecording, setIsRecording] = useState(false);
  //const { speechRecognitionAvailable, startSpeechRecognition, stopSpeechRecognition } = useSpeechRecognition({ language });
  let { speechRecognitionAvailable, startSpeechRecognition, stopSpeechRecognition } = useSpeechRecognition({ language });
  const [transcript, setTranscript] = useState('');
  const transcriptRef = useRef('');
  const isRecordingRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const { processAudioComplete } = useAudioProcessor(0.01, 100);
  
  speechRecognitionAvailable = false;

  const startRecording = useCallback(
    async (onStartRecording, onStopRecording, options = []) => {
      if (!isSupported || !permissionGranted) {
        handleError({
          code: 'ERROR_START_RECORDING',
          error: new Error('Unsupported or no permission'),
          context: {
            module: 'audio_recorder',
            message: 'Нельзя начать запись',
            isSupported,
            permissionGranted,
            userId,
            action: 'start_recording',
          },
          showToUser: true,
        });
        return;
      }

      let stream = streamRef.current;
      
      // Если потока нет или он неактивен - запрашиваем снова
      if (!stream || !stream.active) {
        try {
          const constraints = {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              ...(isIOS ? {} : {
                sampleRate: 48000,
                channelCount: 1
              })
            }
          };
          
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          streamRef.current = stream;
        } catch (error) {
          const errorMessage = error.name === 'NotAllowedError' 
            ? 'Доступ к микрофону запрещен' 
            : 'Не удалось открыть микрофон';
            
          onStopRecording(null, { error: errorMessage });
          handleError({
            code: 'ERROR_GETTING_STREAM',
            error,
            context: {
              module: 'audio_recorder',
              message: 'Ошибка получения аудиопотока',
              userId,
              action: 'stream_error',
              errorName: error.name,
            },
            showToUser: true,
          });
          return;
        }
      }

      // Активируем треки
      stream.getTracks().forEach(track => (track.enabled = true));

      try {
        // Для iOS Safari может потребоваться другой MIME-тип
        const mimeType = isIOS && MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/webm';
          
        const mediaRecorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];
        setTranscript('');
        transcriptRef.current = '';

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };

        mediaRecorder.onerror = (event) => {
          handleError({
            code: 'ERROR_MEDIARECORDER',
            error: event.error,
            context: {
              module: 'audio_recorder',
              message: 'Ошибка MediaRecorder',
              userId,
              action: 'recording_error',
            },
            showToUser: true,
          });
        };

        mediaRecorder.onstop = async () => {
          // Деактивируем треки но НЕ останавливаем их
          stream.getTracks().forEach(track => (track.enabled = false));
          
          // Небольшая задержка для завершения записи
          await new Promise(resolve => setTimeout(resolve, 300));

          const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
          const completeBlob = new Blob(chunksRef.current, { type: mimeType });

          if (!completeBlob || completeBlob.size === 0) {
            onStopRecording(null, { error: 'Пустая запись' });
            handleError({
              code: 'ERROR_EMPTY_BLOB',
              error: new Error('Пустой Blob'),
              context: {
                module: 'audio_recorder',
                message: 'Пустой Blob при остановке записи',
                userId,
                action: 'blob_empty',
              },
              showToUser: true,
            });
            return;
          }

          // Проверка распознавания речи
          if (speechRecognitionAvailable && transcriptRef.current) {
            const matchedOptionIndex = compareText(transcriptRef.current, options, language);
            if (matchedOptionIndex !== null) {
              onStopRecording(null, { 
                recognizedText: transcriptRef.current, 
                matchedOptionIndex 
              });
              return;
            }
          }

          try {
            const { wavBlob, duration } = await processAudioComplete(completeBlob);
            onStopRecording(wavBlob, { duration, recognizedText: transcriptRef.current || '' });
          } catch (error) {
            handleError({
              code: 'ERROR_PROCESSING_AUDIO',
              error,
              context: {
                module: 'audio_recorder',
                message: 'Ошибка обработки аудио',
                userId,
                action: 'audio_processing_error',
              },
              showToUser: false,
            });
            // Отправляем оригинальный blob если обработка не удалась
            onStopRecording(completeBlob, { 
              error: 'Ошибка обработки аудио, отправляем оригинальный Blob',
              duration: 0 
            });
          }
        };

        // Запускаем запись
        mediaRecorder.start();
        
        // Запускаем распознавание речи если доступно
        if (speechRecognitionAvailable && source !== 'examScreen') {
          startSpeechRecognition((finalText) => {
            setTranscript(finalText);
            transcriptRef.current = finalText;
          });
        }

        setIsRecording(true);
        isRecordingRef.current = true;
        onStartRecording?.();
        
      } catch (error) {
        handleError({
          code: 'ERROR_MEDIARECORDER_INIT',
          error,
          context: {
            module: 'audio_recorder',
            message: 'Не удалось инициализировать MediaRecorder',
            userId,
            action: 'init_error',
          },
          showToUser: true,
        });
        onStopRecording(null, { error: 'Ошибка инициализации записи' });
      }
    },
    [permissionGranted, isSupported, speechRecognitionAvailable, startSpeechRecognition, userId, language, source, isIOS]
  );

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      const state = mediaRecorderRef.current.state;
      
      if (state === 'recording') {
        setIsRecording(false);
        isRecordingRef.current = false;
        
        if (source !== 'examScreen') {
          stopSpeechRecognition();
        }
        
        mediaRecorderRef.current.stop();
      } else {
        handleError({
          code: 'ERROR_STOP_RECORDING_INVALID_STATE',
          error: new Error('MediaRecorder not in recording state'),
          context: {
            module: 'audio_recorder',
            message: `MediaRecorder в состоянии: ${state}`,
            state,
            isRecording,
            userId,
            action: 'stop_recording',
          },
          showToUser: false,
        });
      }
    }
  }, [isRecording, stopSpeechRecognition, userId, source]);

  const stateValue = { isRecording, transcript, startRecording, stopRecording };

  return (
    <AudioRecorderStateContext.Provider value={stateValue}>
      {children}
    </AudioRecorderStateContext.Provider>
  );
};

export const useAudioRecorderConfig = () => useContext(AudioRecorderConfigContext);
export const useAudioRecorderState = () => useContext(AudioRecorderStateContext);




/*
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useSpeechRecognition } from '../components/AudioRecorder/useSpeechRecognition';
import { useAudioProcessor } from '../components/AudioRecorder/useAudioProcessor';
import { compareText } from '../components/AudioRecorder/compareText';
import { handleError } from './errors/errorUtils';

const AudioRecorderConfigContext = createContext();
const AudioRecorderStateContext = createContext();

const isMediaRecorderSupported = () => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm');

export const AudioRecorderProvider = ({ children, userId, language = 'en', source }) => {
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const streamRef = useRef(null);

  useEffect(() => {
    setIsSupported(isMediaRecorderSupported());
  }, []);

  useEffect(() => {
    async function requestMicrophonePermission() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;
        stream.getTracks().forEach(track => (track.enabled = false));
        setPermissionGranted(true);
      } catch (error) {
        setPermissionGranted(false);
        handleError({
          code: 'ERROR_MIC_PERMISSION',
          error,
          context: {
            module: 'audio_recorder',
            message: 'Ошибка доступа к микрофону',
            userId,
            action: 'permission_error',
          },
          showToUser: false,
        });
      }
    }

    if (isSupported) requestMicrophonePermission();

    const handleVisibilityChange = () => {
      if (document.hidden && streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [isSupported, userId]);

  const configValue = { permissionGranted, isSupported, streamRef };

  return (
    <AudioRecorderConfigContext.Provider value={configValue}>
      <AudioRecorderStateProvider userId={userId} language={language} source={source}>
        {children}
      </AudioRecorderStateProvider>
    </AudioRecorderConfigContext.Provider>
  );
};

const AudioRecorderStateProvider = ({ children, userId, language, source }) => {
  const { permissionGranted, isSupported, streamRef } = useAudioRecorderConfig();
  const [isRecording, setIsRecording] = useState(false);
  const { speechRecognitionAvailable, startSpeechRecognition, stopSpeechRecognition } = useSpeechRecognition({ language });
  const [transcript, setTranscript] = useState('');
  const transcriptRef = useRef('');
  const isRecordingRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const { processAudioComplete } = useAudioProcessor(0.01, 100);
  const [isSpeechRecognitionEnabled, setIsSpeechRecognitionEnabled] = useState(false);
  
  //console.log("Разрешения speechRecognitionAvailable", speechRecognitionAvailable);

  const startRecording = useCallback(
    async (onStartRecording, onStopRecording, options = []) => {
      if (!isSupported || !permissionGranted) {
        handleError({
          code: 'ERROR_START_RECORDING',
          error: new Error('Unsupported or no permission'),
          context: {
            module: 'audio_recorder',
            message: 'Нельзя начать запись',
            isSupported,
            permissionGranted,
            userId,
            action: 'start_recording',
          },
          showToUser: false,
        });
        return;
      }

      let stream = streamRef.current;
      if (!stream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          streamRef.current = stream;
        } catch (error) {
          onStopRecording(null, { error: 'Не удалось открыть микрофон' });
          handleError({
            code: 'ERROR_GETTING_STREAM',
            error,
            context: {
              module: 'audio_recorder',
              message: 'Ошибка получения аудиопотока',
              userId,
              action: 'stream_error',
            },
            showToUser: false,
          });
          return;
        }
      }

      stream.getTracks().forEach(track => (track.enabled = true));
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      setTranscript('');
      transcriptRef.current = '';

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => (track.enabled = false));
        await new Promise(resolve => setTimeout(resolve, 500));

        const completeBlob = new Blob(chunksRef.current, { type: 'audio/webm' });

        if (!completeBlob || completeBlob.size === 0) {
          onStopRecording(null, { error: 'Пустой Blob' });
          handleError({
            code: 'ERROR_EMPTY_BLOB',
            error: new Error('Пустой Blob'),
            context: {
              module: 'audio_recorder',
              message: 'Пустой Blob при остановке записи',
              userId,
              action: 'blob_empty',
            },
            showToUser: false,
          });
          return;
        }
		
        if (speechRecognitionAvailable && transcriptRef.current) {
          const matchedOptionIndex = compareText(transcriptRef.current, options, language);
          if (matchedOptionIndex !== null) {
            onStopRecording(null, { recognizedText: transcriptRef.current, matchedOptionIndex });
            return;
          }
        }

        try {
          const { wavBlob, duration } = await processAudioComplete(completeBlob);
          onStopRecording(wavBlob, { duration, recognizedText: transcriptRef.current || '' });
        } catch (error) {
          handleError({
            code: 'ERROR_PROCESSING_AUDIO',
            error,
            context: {
              module: 'audio_recorder',
              message: 'Ошибка обработки аудио',
              userId,
              action: 'audio_processing_error',
            },
            showToUser: false,
          });
          onStopRecording(completeBlob, { error: 'Ошибка обработки аудио, отправляем оригинальный Blob' });
        }
      };

      mediaRecorder.start();
      if (speechRecognitionAvailable && source !== 'examScreen') {
        startSpeechRecognition((finalText) => {
          setTranscript(finalText);
          transcriptRef.current = finalText;
        });
      }

      setIsRecording(true);
      isRecordingRef.current = true;
      onStartRecording?.();
    },
    [permissionGranted, isSupported, speechRecognitionAvailable, startSpeechRecognition, userId, language, source]
  );

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording && mediaRecorderRef.current.state === 'recording') {
      setIsRecording(false);
      isRecordingRef.current = false;
      if (source !== 'examScreen') {
        stopSpeechRecognition();
      }
      mediaRecorderRef.current.stop();
    } else {
      handleError({
        code: 'ERROR_STOP_RECORDING_INVALID_STATE',
        error: new Error('MediaRecorder not in recording state'),
        context: {
          module: 'audio_recorder',
          message: 'MediaRecorder не в состоянии recording или запись не активна',
          state: mediaRecorderRef.current?.state,
          isRecording,
          userId,
          action: 'stop_recording',
        },
        showToUser: false,
      });
    }
  }, [isRecording, stopSpeechRecognition, userId, source]);

  const stateValue = { isRecording, transcript, startRecording, stopRecording };

  return (
    <AudioRecorderStateContext.Provider value={stateValue}>
      {children}
    </AudioRecorderStateContext.Provider>
  );
};

export const useAudioRecorderConfig = () => useContext(AudioRecorderConfigContext);
export const useAudioRecorderState = () => useContext(AudioRecorderStateContext);
*/
